'use client';

import { createContext, useCallback, useContext, useState } from 'react';
import { useColorScheme } from '@mui/material/styles';
import { BRAND_COLORS } from '@/theme/theme';
import { useSettingsContext } from '../context';
import { ThemeTransitionOverlay } from './theme-transition-overlay';

// ----------------------------------------------------------------------

type ThemeMode = 'light' | 'dark';

type ThemeTransitionContextValue = {
  /** Start full-page theme transition to target mode */
  requestThemeTransition: (targetMode: ThemeMode) => void;
  /** True while the overlay animation is running */
  isTransitioning: boolean;
};

const ThemeTransitionContext = createContext<ThemeTransitionContextValue | undefined>(undefined);

// ----------------------------------------------------------------------

const TRANSITION_DURATION_MS = 300;

/** Target mode background colors for the overlay (match theme) */
const MODE_BG = {
  dark: BRAND_COLORS.darkTeal,
  light: '#F7F8FA',
} as const;

export function ThemeTransitionProvider({ children }: { children: React.ReactNode }) {
  const { mode, setMode } = useColorScheme();
  const settings = useSettingsContext();
  const [state, setState] = useState<{ isTransitioning: boolean; fromMode: ThemeMode | null }>({
    isTransitioning: false,
    fromMode: null,
  });

  const requestThemeTransition = useCallback(
    (targetMode: ThemeMode) => {
      if (targetMode === mode) {
        settings.onUpdateField('colorScheme', targetMode);
        return;
      }
      // Switch theme first; overlay (old theme color) will wipe away left→right, revealing new theme
      setMode(targetMode);
      settings.onUpdateField('colorScheme', targetMode);
      setState({ isTransitioning: true, fromMode: (mode as ThemeMode | undefined) ?? null });
    },
    [mode, settings, setMode]
  );

  const onTransitionEnd = useCallback(() => {
    setState({ isTransitioning: false, fromMode: null });
  }, []);

  const value: ThemeTransitionContextValue = {
    requestThemeTransition,
    isTransitioning: state.isTransitioning,
  };

  return (
    <ThemeTransitionContext.Provider value={value}>
      {children}
      {state.isTransitioning && state.fromMode !== null && (
        <ThemeTransitionOverlay
          backgroundColor={MODE_BG[state.fromMode]}
          durationMs={TRANSITION_DURATION_MS}
          onAnimationEnd={onTransitionEnd}
        />
      )}
    </ThemeTransitionContext.Provider>
  );
}

export function useThemeTransition() {
  const ctx = useContext(ThemeTransitionContext);
  if (!ctx) throw new Error('useThemeTransition must be used inside ThemeTransitionProvider');
  return ctx;
}
